import { lighten } from 'polished';
import styled, { css } from 'styled-components';

import { FormValidationState } from 'utils/form';

import theme from '../theme';

export interface IValidationState {
  validation?: FormValidationState;
  hideCountrySelector?: boolean;
}

interface IHasFooter {
  hasFooter?: boolean;
}

interface IReadOnly {
  readOnly?: boolean;
}

const BASE_FONT_SIZE = '0.9375rem';
export const FLOATING_LABEL_INPUT_WRAPPER_MARGIN = css`
  margin: 0.65rem 0 1.25rem;
`;

export const FloatingLabelInputWrapper = styled.div`
  background-color: ${theme.backgroundColor};
  border-radius: ${Styles.borderRadius};
  display: inline-block;
  width: 100%;
  ${FLOATING_LABEL_INPUT_WRAPPER_MARGIN}
  position: relative;
`;

const transformedLabelStyle = css`
  background: ${theme.backgroundColor};
  border-radius: ${Styles.borderRadius} ${Styles.borderRadius} 0 0;
  opacity: 1;
  padding: 2px 10px;
  transform: scale(0.75) translate(-0.625rem, -1.6rem);
`;

const placeholderStyle = css`
  color: ${theme.placeholderColor};
  font-family: ${Styles.fontFamily.body};
  font-size: ${BASE_FONT_SIZE};
  font-weight: normal;
`;

export const Label = styled.label<{ hasValue: boolean }>`
  bottom: 0.9rem; /* need to figure out how to handle disabled states as the  background color is odd. */
  left: 1rem;
  position: absolute;
  user-select: none;
  z-index: ${Styles.zIndex.below};

  ${placeholderStyle}

  transform-origin: top left;
  transform: none;
  transition: all 200ms ease-out;

  ${props => props.hasValue && transformedLabelStyle}

  input:focus + &,
  input[disabled] + &,
  input[type=date][value=''] + &,
  input[type=date]:empty + &,
  input:placeholder-shown + & {
    ${transformedLabelStyle}
  }

  input:-webkit-autofill + &,
  input:-webkit-autofill:focus + & {
    ${transformedLabelStyle}
  }
`;

export const TextAreaLabel = styled.label<{ hasValue: boolean }>`
  top: 0.6rem;
  left: 1rem;
  position: absolute;
  user-select: none;
  z-index: ${Styles.zIndex.below};

  ${placeholderStyle}

  transform-origin: top left;
  transform: none;
  transition: all 200ms ease-out;

  ${props => props.hasValue && transformedLabelStyle}

  textarea:focus + &,
  textarea[disabled] + &,
  textarea[type=date][value=''] + &,
  textarea[type=date]:empty + &,
  textarea:placeholder-shown + & {
    ${transformedLabelStyle}
  }

  textarea:-webkit-autofill + &,
  textarea:-webkit-autofill:focus + & {
    ${transformedLabelStyle}
  }
`;

const IconContainerStyle = css`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  right: 1rem;
  width: 0.8rem;
  height: 0.8rem;
  pointer-events: none;

  > svg {
    transform: translateY(-50%);
  }
`;

export const IconContainer = styled.div<IValidationState>`
  ${IconContainerStyle}

  > svg {
    fill: ${({ validation }) =>
      validation === FormValidationState.INVALID
        ? Styles.color.error
        : validation === FormValidationState.VALID
        ? Styles.color.validated
        : Styles.color.grey.two};
    opacity: ${({ validation }) =>
      validation === FormValidationState.INVALID
        ? 1
        : validation === FormValidationState.VALID
        ? 1
        : 0};
  }
`;

export const IconContainerSelect = styled(IconContainer)`
  right: 3rem;
`;

export const IconContainerSelectArrow = styled.div`
  ${IconContainerStyle}
  right: 2rem;
  top: 1.25rem;
`;

export const Message = styled.div<IValidationState>`
  position: absolute;
  color: ${({ validation }) =>
    validation === FormValidationState.INVALID
      ? Styles.color.error
      : validation === FormValidationState.VALID
      ? Styles.color.validated
      : Styles.color.grey.two};
  font-family: ${Styles.fontFamily.body};
  font-size: 0.7rem;
  margin-top: 2px;
`;

const getBorderForValidationState = (validation: FormValidationState | undefined): string => {
  let color: string;
  switch (validation) {
    case FormValidationState.INVALID:
      color = Styles.color.error;
      break;
    case FormValidationState.VALID:
      color = Styles.color.validated;
      break;
    default:
      color = theme.inactiveBorderColor;
      break;
  }
  return `1px solid ${color}`;
};

export const StyledInput = styled.input<IValidationState & IHasFooter>`
  appearance: none;
  background: none;
  border: ${({ validation }) => getBorderForValidationState(validation)};
  border-radius: ${p =>
    p.hasFooter ? `${Styles.borderRadius} ${Styles.borderRadius} 0 0` : Styles.borderRadius};
  color: ${({ validation }) =>
    validation === FormValidationState.INVALID ? Styles.color.error : Styles.color.black};
  font-family: ${Styles.fontFamily.body};
  font-size: ${BASE_FONT_SIZE};
  height: 3.125rem;
  padding: 1rem 2rem 1rem 1rem;
  position: relative;
  width: 100%;

  :focus {
    outline: none;
    color: ${Styles.color.black};
  }

  &[value='']:not(:placeholder-shown),
  &:placeholder-shown::placeholder {
    ${placeholderStyle}
  }

  &[required] + ${Label}::after {
    content: ' *';
  }

  &[disabled] {
    background: #f3f3f3;
    border: 1px solid ${theme.inactiveBorderColor};
    color: #76756c;
    cursor: not-allowed;
  }
`;

export const StyledTextArea = styled.textarea<IValidationState>`
  appearance: none;
  background: none;
  border: ${({ validation }) => getBorderForValidationState(validation)};
  border-radius: ${Styles.borderRadius};
  color: ${({ validation }) =>
    validation === FormValidationState.INVALID ? Styles.color.error : Styles.color.black};
  font-family: ${Styles.fontFamily.body};
  font-size: ${BASE_FONT_SIZE};
  min-height: 6.25rem;
  padding: 1rem 2.5rem 1rem 1rem;
  position: relative;
  width: 100%;

  resize: vertical;

  :focus {
    outline: none;
    color: ${Styles.color.black};
    border: 1px solid ${Styles.color.accent};
  }

  &[value='']:not(:placeholder-shown),
  &:placeholder-shown::placeholder {
    ${placeholderStyle}
  }

  &[required] + ${TextAreaLabel}::after {
    content: ' *';
  }

  &[disabled] {
    background: #f3f3f3;
    border: 1px solid ${theme.inactiveBorderColor};
    color: #76756c;
    cursor: not-allowed;
  }
`;

/* PhoneNumberInput styles */
const SELECT_WIDTH = 5;
const COUNTRY_CODE_WIDTH = 2;
const COUNTRY_CODE_RIGHT_PADDING = 0.15;

export const FlagEmoji = styled.span`
  display: inline-block;
  margin: 0 0.5rem 0 1rem;
  font-size: 1.25rem;
  pointer-events: none;
`;

export const ArrowDownContainer = styled.div`
  display: inline-block;
  margin: 0.25rem 0 0;
  pointer-events: none;
`;

export const CountryCode = styled.div<IReadOnly>`
  float: left;
  width: ${COUNTRY_CODE_WIDTH}rem;
  font-size: ${BASE_FONT_SIZE};
  padding-right: ${COUNTRY_CODE_RIGHT_PADDING}rem;
  pointer-events: none;
  text-align: right;
`;

export const HiddenSelect = styled.select`
  position: absolute;
  height: 100%;
  width: ${SELECT_WIDTH + COUNTRY_CODE_WIDTH - COUNTRY_CODE_RIGHT_PADDING}rem;
  appearance: none;
  background: none;
  cursor: pointer;
  border: none;
  opacity: 0;

  &[disabled] {
    cursor: not-allowed;

    ~ ${ArrowDownContainer} {
      & > svg {
        fill: #76756c;
      }
    }
  }

  &:focus {
    outline: none;
  }
`;

export const PhoneInput = styled.input<{ hideCountrySelector?: boolean }>`
  position: relative;
  display: inline-block;
  height: 100%;
  width: calc(100% - ${COUNTRY_CODE_WIDTH}rem);
  padding-right: 2.5rem;
  appearance: none;
  background: none;
  border: none;
  color: inherit;
  font-family: ${Styles.fontFamily.body};
  font-size: ${BASE_FONT_SIZE};

  :focus {
    outline: none;
  }

  &[disabled] {
    color: #76756c;
    cursor: not-allowed;
  }

  ${props =>
    props.hideCountrySelector &&
    css`
      padding: 1rem 2rem 1rem 1rem;
    `}
`;

const borderRadiusWithSelect = `0 ${Styles.borderRadius} ${Styles.borderRadius} 0`;

export const NationalNumberWrapper = styled.div<IValidationState>`
  width: 100%;
  border: ${({ validation }) => getBorderForValidationState(validation)};
  border-radius: ${({ hideCountrySelector }) =>
    hideCountrySelector ? Styles.borderRadius : borderRadiusWithSelect};
  border-left: ${({ hideCountrySelector }) => (hideCountrySelector ? '1px solid' : 'none')};
  color: ${({ validation }) =>
    validation === FormValidationState.INVALID ? Styles.color.error : Styles.color.black};
  line-height: 3.125rem;

  :focus-within {
    color: ${Styles.color.black};
    border-color: ${({ validation }) =>
      validation === FormValidationState.INVALID ? Styles.color.error : Styles.color.primary};
  }

  & ~ ${Label} {
    color: ${({ validation }) =>
      validation === FormValidationState.INVALID ? Styles.color.error : Styles.color.black};
  }
`;

export const SelectWrapper = styled.div<IValidationState & IReadOnly>`
  display: flex;
  align-items: center;
  float: left;
  height: 100%;
  width: ${SELECT_WIDTH}rem;
  border-top-left-radius: ${Styles.borderRadius};
  border-bottom-left-radius: ${Styles.borderRadius};
  border: ${({ validation }) => getBorderForValidationState(validation)};
  border-right: 1px solid transparent;

  & > div > svg {
    fill: ${({ validation }) =>
      validation === FormValidationState.INVALID ? Styles.color.error : Styles.color.black};
  }

  ${({ readOnly }) =>
    readOnly &&
    `
    background: #f3f3f3;
    color: #76756c;
    cursor: not-allowed;
  `};

  :focus-within {
    border: 1px solid ${theme.activeBorderColor};

    & > *,
    & > * > * {
      color: ${Styles.color.black};
    }

    & > div > svg {
      fill: ${Styles.color.black};
    }

    ~ ${NationalNumberWrapper} {
      border-color: ${theme.inactiveBorderColor};
    }
  }

  :not(:focus-within) {
    border-right-color: transparent !important;
  }
`;

export const PhoneInputContainer = styled.div<IReadOnly>`
  position: relative;
  display: flex;
  height: 3.125rem;
  width: 100%;

  ${({ readOnly }) =>
    readOnly &&
    `
    & > div {
      background: #f3f3f3;
      color: #76756c;
    }
  `};

  :focus-within {
    & > div {
      border-color: ${theme.activeBorderColor};
    }
  }
`;

export const VerificationBadgeContainer = styled.div`
  position: absolute;
  padding-right: 0.5rem;
  top: 50%;
  right: 2rem;
  transform: translateY(-50%);
  opacity: 0;
  visibility: hidden;

  :hover {
    opacity: 1;
    visibility: visible;
  }
`;

export const VerificationIconContainer = styled.div<IValidationState>`
  ${IconContainerStyle}
  height: 1rem;
  width: 1rem;
  pointer-events: auto;

  > svg {
    fill: ${({ validation }) =>
      validation === FormValidationState.VALID ? Styles.color.validated : Styles.color.grey.two};
    opacity: 1;
  }

  &:focus
    ~ ${VerificationBadgeContainer},
    &:focus-within
    ~ ${VerificationBadgeContainer},
    &:hover
    ~ ${VerificationBadgeContainer} {
    opacity: 1;
    visibility: visible;
    transition: opacity 0.15s linear;
  }
`;

export const VerificationBadge = styled.div`
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: ${BASE_FONT_SIZE};
`;

export const UnverifiedBadge = styled(VerificationBadge)`
  background: ${lighten(0.5, Styles.color.grey.two)};
  color: ${Styles.color.grey.two};
`;

export const SuccessBadge = styled(VerificationBadge)`
  background: ${lighten(0.5, Styles.color.validated)};
  color: ${Styles.color.validated};
`;
